/**
 * RecordPatientPaymentModal.tsx
 *
 * Unified "Record a Payment" modal matching legacy
 * `html/lib/payment-details-record.php`. Handles both:
 *   - Create mode (paymentId null/undefined): manual payment entry.
 *   - View / edit mode (paymentId set): loads the existing record, locks
 *     type/amount/date/method/reference per legacy disable rule, keeps
 *     linkage / memo / products editable, and surfaces the full action
 *     toolbar (Void / Un-Void / Refund / Convert / Receipt preview /
 *     email / sign).
 *
 * Never triggers a Forward gateway intent — this is the manual pathway.
 * Runs through the same canonical `POST /v5/patients/{id}/payments`
 * endpoint; when `paymentID` is included in the payload the service
 * takes the update branch.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, CreditCard, Gift, Link2, Loader2, Mail, Tag } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PatientLink } from '@/components/ui/PatientLink';
import {
  createPatientPayment,
  chargeForwardPayment,
  listPaymentMethodOptions,
  getPaymentDetail,
  voidPayment,
  voidPaymentTransaction,
  unvoidPayment,
  refundPaymentTransaction,
  listReceiptTemplates,
  previewReceiptPdfBlob,
  emailReceipt,
  previewReceiptEmailHtml,
  signReceiptPdfUrl,
  receiptDirectLink,
  type ReceiptTemplate,
  listBillingMethods,
  listDiscounts,
  type BillingMethod,
  type Merchant,
  type PaymentDetail,
  type PaymentMethodOption,
  type PaymentTerminal,
  type PaymentDiscount,
} from '@/services/payments.service';
import { MerchantSelector } from '@/components/payments/MerchantSelector';
import { PaymentSourceSelector } from '@/components/payments/PaymentSourceSelector';
import { ForwardBillingMethodCapture } from '@/components/payments/ForwardBillingMethodCapture';
import { getPatient, listPatientFacilities } from '@/services/patients.service';
import { appointmentsService } from '@/services/appointments.service';
import { getPatientProductTypes } from '@/services/patientProductTypes.service';
import { getErrorMessage } from '@/services/apiClient';
import {
  ProductGroupCard,
  toArray,
  type ProductGroup,
  type ProductItem,
  type ProductTypesResponse,
} from '@/components/patients/PatientProductsDisplay';
import { EnrollmentLinkageFields } from '@/components/memberships/EnrollmentLinkageFields';
import { RecurringDetailModal } from '@/pages/memberships/RecurringDetailModal';
import { GiftCardSelector, type AppliedGiftCard } from '@/components/payments/GiftCardSelector';
import {
  PaymentProductsSection,
  computeLineTotal,
  type ProductLine,
} from '@/components/payments/PaymentProductsSection';
import { PaymentShippingSection } from '@/components/payments/PaymentShippingSection';
import { useShippingFees } from '@/components/payments/useShippingFees';
import { SignaturePadField, type SignaturePadFieldHandle } from '@/components/payments/SignaturePadField';
import {
  formatCalendarDate,
  formatPaymentDate,
  getPaymentStatusDisplay,
  resolveSaveMethodName,
} from './paymentsShared';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { useAuth } from '@/hooks/useAuth';
import GiftCardDetailModal from '@/pages/gift-cards/GiftCardDetailModal';
import { todayInTz, toLocaleStringInTz, toLocaleTimeStringInTz } from '@/lib/facilityTimezone';
import { DatePicker } from '@/components/ui/DatePicker';

/** Legacy parity: status → action mapping from load-payment-transaction-list-authnet.php lines 331-356. */
const VOID_STATUSES = ['authorized', 'submitted_for_settlement', 'capturedPendingSettlement', 'refundPendingSettlement', 'pending'];
const REFUND_STATUSES = ['settling', 'settled', 'settledSuccessfully'];
const RETRY_STATUSES = ['declined', 'Declined'];

/** Shown before retrying a payment that has no product, package or membership. */
const NO_PRODUCT_RETRY_CONFIRM =
  'No product is currently attached to this payment. Please confirm before proceeding.';

function resolveTransactionAction(
  status: string | null | undefined,
  transactionID: string | null | undefined,
): 'void' | 'refund' | 'retry' | null {
  if (!transactionID || transactionID === '0') return 'retry';
  const s = (status ?? '').trim();
  if (VOID_STATUSES.includes(s)) return 'void';
  if (REFUND_STATUSES.includes(s)) return 'refund';
  if (RETRY_STATUSES.includes(s)) return 'retry';
  return null;
}

/** Statuses the Transactions table badges green: the money went through. */
function isSuccessStatus(status: string | null | undefined): boolean {
  const s = (status ?? '').trim().toLowerCase();
  return s.includes('settle') || s === 'captured' || s === 'approved';
}

function formatStatusText(status: string): string {
  const map: Record<string, string> = {
    settledSuccessfully: 'Settled',
    capturedPendingSettlement: 'Pending',
    refundPendingSettlement: 'Refund Pending',
    refundSettledSuccessfully: 'Refunded',
    submitted_for_settlement: 'Submitted For Settlement',
  };
  if (map[status]) return map[status];
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
}

/** Legacy parity: manual-record payment types (identical to payment-details-record.php). */
const MANUAL_PAYMENT_TYPE_OPTIONS = [
  'Payment',
  'Deposit',
  'Consult Fee',
  'Refund',
  'Payment Adjustment',
  'Discount',
] as const;

export interface RecordPatientPaymentModalProps {
  patientId: number;
  patientName?: string;
  /**
   * When set, the modal loads the payment for view / edit. When undefined
   * (or null), the modal runs in create mode.
   */
  paymentId?: number | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  /**
   * Opens the modal with an action panel already showing. Used by
   * ChargePaymentModal's "Send Receipt" shortcut so staff land on the receipt
   * dialog straight from the charge result instead of re-navigating to the
   * payment and clicking Receipt. Only honored in edit mode (a receipt needs a
   * persisted payment). Closing the panel leaves the detail modal open.
   */
  initialPanel?: 'receipt';
}

function formatCurrency(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value ?? 0);
}

function formatDate(value: string | null | undefined, tz?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return toLocaleStringInTz(date, 'en-US', tz ? { timeZone: tz } : undefined);
}

/** Long-form calendar date ("July 31, 2026") — no timezone math. */
function formatDateOnly(value: string | null | undefined): string {
  return formatCalendarDate(value, { month: 'long', day: 'numeric', year: 'numeric' });
}

/* ── Filter products by payment ────────────────────────────────────── */

function filterProductsByPayment(
  data: ProductTypesResponse,
  paymentId: number,
): { types: ProductGroup[]; others: ProductGroup[] } {
  const filterItems = (items: ProductItem[]) =>
    items.filter((i) => i.paymentID != null && i.paymentID === paymentId);

  function filterGroup(group: ProductGroup): ProductGroup | null {
    const available = filterItems(toArray<ProductItem>(group.available));
    const appointments = filterItems(toArray<ProductItem>(group.appointments));
    const comped = filterItems(toArray<ProductItem>(group.comped));
    const purchase = filterItems(toArray<ProductItem>(group.purchase));
    const unavailable = filterItems(toArray<ProductItem>(group.unavailable));

    const total = available.length + appointments.length
      + comped.length + purchase.length + unavailable.length;
    if (total === 0) return null;

    const usedQty = unavailable
      .filter((p) => p.usedDate && p.usedDate !== '0000-00-00 00:00:00')
      .reduce((s, p) => s + p.quantity, 0);
    const expiredOrDeleted = unavailable.filter(
      (p) => !p.usedDate || p.usedDate === '0000-00-00 00:00:00',
    );
    const expireQty = expiredOrDeleted
      .filter((p) => !p.deleteDate || p.deleteDate === '0000-00-00 00:00:00')
      .reduce((s, p) => s + p.quantity, 0);
    const deleteQty = expiredOrDeleted
      .filter((p) => p.deleteDate && p.deleteDate !== '0000-00-00 00:00:00')
      .reduce((s, p) => s + p.quantity, 0);

    return {
      ...group,
      available,
      upcoming: [],
      appointments,
      comped,
      compedQuantity: comped.reduce((s, p) => s + p.quantity, 0),
      purchase,
      unavailable,
      availableQuantity: available.reduce((s, p) => s + p.quantity, 0),
      upcomingQuantity: 0,
      appointmentsQuantity: appointments.reduce((s, p) => s + p.quantity, 0),
      usedQuantity: usedQty,
      expireQuantity: expireQty,
      deleteQuantity: deleteQty,
    };
  }

  const rawTypes = data?.data?.['product-types'] ?? {};
  const rawOthers = data?.data?.['product-others'] ?? {};

  const types = Object.values(rawTypes)
    .map(filterGroup)
    .filter((g): g is ProductGroup => g !== null);
  const others = Object.values(rawOthers)
    .map(filterGroup)
    .filter((g): g is ProductGroup => g !== null);

  return { types, others };
}

/* ── Collapsible Section ──────────────────────────────────────────── */

function CollapsibleSection({ title, defaultOpen = false, children }: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg border border-slate-200">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 bg-slate-50 px-4 py-2 text-left"
      >
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${open ? '' : '-rotate-90'}`} />
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      </button>
      {open && <div className="border-t border-slate-200">{children}</div>}
    </div>
  );
}

/**
 * The edit form's applied cards. The API's `appliedGiftCards` gives one
 * amount per card (its net on the payment, or the share a declined payment
 * holds for its retry); `giftCards` lists every ledger row, restores
 * included, so here it only supplies each card's serial, title and balance
 * (newest row first). Saving the form unchanged then spends and restores
 * nothing. A response without `appliedGiftCards` maps row by row as before.
 */
function mapDetailGiftCardsToApplied(detail: PaymentDetail): AppliedGiftCard[] {
  const toApplied = (gc: PaymentDetail['giftCards'][number], amount: number): AppliedGiftCard => ({
    giftCard: {
      giftCardId: gc.giftCardID,
      paymentsCreditID: gc.giftCardID,
      accountID: 0,
      serialNumber: gc.serialNumber ?? '',
      creditAmount: amount + gc.balanceAmount,
      redeemedAmount: gc.redeemedAmount,
      remainingBalance: gc.balanceAmount,
      title: gc.title,
      void: '0',
      createDate: gc.createDate,
      creditTypeName: gc.title,
    },
    amount,
  });

  if (!detail.appliedGiftCards) {
    return detail.giftCards.map((gc) => toApplied(gc, gc.amount));
  }

  return detail.appliedGiftCards.flatMap((applied) => {
    const row = detail.giftCards.find((gc) => gc.giftCardID === applied.giftCardID);
    return row && applied.amount > 0 ? [toApplied(row, applied.amount)] : [];
  });
}

type ActionPanel =
  | null
  | { kind: 'receipt' }
  | { kind: 'sign'; signerName: string; signedPdfUrl: string | null }
  | { kind: 'refund'; transactionId: number; originalAmount: number };

export function RecordPatientPaymentModal({
  patientId,
  patientName,
  paymentId,
  open,
  onClose,
  onSaved,
  initialPanel,
}: RecordPatientPaymentModalProps) {
  const queryClient = useQueryClient();
  const userTz = useUserTimezone();
  const isEditMode = typeof paymentId === 'number' && paymentId > 0;
  const activePaymentId = isEditMode ? (paymentId as number) : null;

  // A gift card on this payment (sold on a line, or spent on it) opens its
  // detail modal over this one; same permission as the Gift Cards page.
  const { user: authUser, hasPermission } = useAuth();
  const canManageGiftCards = (authUser?.userLevel ?? 0) > 3 || hasPermission(14);
  const [giftCardModalId, setGiftCardModalId] = useState<number | null>(null);

  // ─── Load detail when editing ─────────────────────────────────────────────
  const { data: detail, isLoading: detailLoading, error: detailError } = useQuery<PaymentDetail>({
    queryKey: ['payment-detail', activePaymentId],
    queryFn: () => getPaymentDetail(activePaymentId as number),
    enabled: open && isEditMode,
  });

  // Derive the canonical display status the SAME way the payments list does
  // (paymentsShared.ts:getPaymentStatusDisplay), which recognizes declined
  // BEFORE the payments.void flag. NOTE: the /payments/{id} detail payload
  // does NOT carry the denormalized `transStatus` the list uses (mapPayment
  // drops it), so we source the gateway status from the latest transaction
  // row — the same data the Transactions sub-table uses to render "Declined".
  // `detail.transactions` is sorted desc by createDate, so [0] is the latest.
  // Without this, a declined charge (stored with payments.void=1 by design)
  // has an empty transStatus, falls through to the void flag, and mislabels
  // as "Voided" at the top of the modal while the sub-table says "Declined".
  const statusDisplay = useMemo(() => {
    if (!detail) return null;
    const rawLatest = (detail.transactions[0]?.status ?? '').trim();
    const lower = rawLatest.toLowerCase();
    // PTA status casing varies ('declined' vs 'Declined' — see RETRY_STATUSES),
    // so normalize the two terminal states getPaymentStatusDisplay keys on to
    // its canonical lowercase forms; leave other statuses (e.g. camelCase
    // settledSuccessfully) untouched.
    const latestTransStatus =
      lower === 'declined' ? 'declined'
        : lower === 'voided' ? 'voided'
          : rawLatest || null;
    return getPaymentStatusDisplay({ void: detail.void, transStatus: latestTransStatus });
  }, [detail]);
  const isVoid = statusDisplay?.voided ?? false;
  const isDeclined = statusDisplay?.declined ?? false;

  // Toolbar Void Payment routing: pick the latest PTA with positive amount
  // whose status indicates the gateway holds money that needs reversing.
  // `detail.transactions` is sorted desc by createDate per
  // PaymentExtrasController::show:188, so the first match is the latest.
  // Refund rows (amount < 0) are skipped because voiding a prior refund
  // doesn't make sense — they represent money already returned.
  const latestCapturedPta = useMemo(() => {
    for (const t of detail?.transactions ?? []) {
      if ((t.amount ?? 0) <= 0) continue;
      const status = (t.status ?? '').trim();
      if (VOID_STATUSES.includes(status) || REFUND_STATUSES.includes(status)) {
        return t;
      }
    }
    return null;
  }, [detail?.transactions]);

  // Resolve the toolbar action the SAME way the per-row Action column and
  // legacy load-payment-transaction-list-authnet.php (lines 331-356) do:
  // run resolveTransactionAction against the latest captured positive PTA.
  // A settled PTA resolves to 'refund' (open the Refund dialog); an
  // unsettled/pending one resolves to 'void' (gateway cancel). This drives
  // both the button label and the action so settled payments never read
  // "Void Payment" by accident.
  const primaryToolbarAction = useMemo(
    () => resolveTransactionAction(
      latestCapturedPta?.status,
      latestCapturedPta?.transactionID,
    ),
    [latestCapturedPta],
  );

  // The retryable transaction is the latest one whose resolved action is
  // 'retry' (declined or never reached the gateway). The Transactions table
  // only surfaces an action on the first actionable row, so we mirror that:
  // if the first actionable transaction is a retry, the payment can be retried.
  const retryableTransaction = useMemo(() => {
    for (const t of detail?.transactions ?? []) {
      const action = resolveTransactionAction(t.status, t.transactionID);
      if (action) return action === 'retry' ? t : null;
    }
    return null;
  }, [detail?.transactions]);
  const canRetry = retryableTransaction !== null;

  const toolbarVoidLabel = primaryToolbarAction === 'refund' ? 'Refund Payment' : 'Void Payment';
  const toolbarVoidConfirm = 'Void this payment at the gateway?';

  // ─── Form state (mirror legacy fields) ────────────────────────────────────
  const [amount, setAmount] = useState('');
  // "Today" in the user's zone, not UTC — see todayInTz. This value is stored
  // verbatim as payments.dtmDate, so a UTC day would misdate every payment
  // taken after ~4/5 PM Pacific.
  const [date, setDate] = useState(() => todayInTz(userTz));
  const [payType, setPayType] = useState('Payment');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [memo, setMemo] = useState('');
  const [planId, setPlanId] = useState('');
  const [quoteId, setQuoteId] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [giftCards, setGiftCards] = useState<AppliedGiftCard[]>([]);
  const [productLines, setProductLines] = useState<ProductLine[]>([]);
  const [amountFromProducts, setAmountFromProducts] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ─── Retry (declined payment) state ───────────────────────────────────────
  // Legacy parity: reviewPaymentCharge re-charged the existing payment with the
  // chosen card (saved or newly added). We re-charge the same paymentID via
  // charge-forward, which un-voids the row on success.
  const [showRetry, setShowRetry] = useState(false);
  const [retryToken, setRetryToken] = useState('new');
  const [retrySelectedMethod, setRetrySelectedMethod] = useState<BillingMethod | null>(null);
  const [retryMerchantId, setRetryMerchantId] = useState<number | null>(null);
  const [retryResult, setRetryResult] = useState<{ kind: 'success' | 'declined'; message: string } | null>(null);

  // ─── Action panel state ───────────────────────────────────────────────────
  const [panel, setPanel] = useState<ActionPanel>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [membershipModalId, setMembershipModalId] = useState<number | null>(null);

  // ─── Reference data ───────────────────────────────────────────────────────
  const { data: paymentMethodOptions } = useQuery({
    queryKey: ['payment-method-options'],
    queryFn: listPaymentMethodOptions,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const selectedMethod = useMemo(
    () => (paymentMethodOptions ?? []).find((option) => option.paymentMethodID === Number(paymentMethodId)) ?? null,
    [paymentMethodId, paymentMethodOptions],
  );

  // Legacy parity (payment-details-record.php): the Type / Method selects must
  // always show the value stored on the payment, even when it falls outside the
  // active option lists (e.g. gateway / recurring charges, or a method that was
  // since deactivated). When the stored value isn't already an option we render
  // a synthetic <option> so the control shows it instead of "Select One...".
  const customPayType = useMemo(() => {
    if (!payType || payType === 'Not Specified') return null;
    return (MANUAL_PAYMENT_TYPE_OPTIONS as readonly string[]).includes(payType) ? null : payType;
  }, [payType]);

  const methodIsKnownOption = useMemo(
    () =>
      paymentMethodId !== '' &&
      (paymentMethodOptions ?? []).some((option) => String(option.paymentMethodID) === paymentMethodId),
    [paymentMethodId, paymentMethodOptions],
  );

  // Display-only fallback when the payment carries a method we can't map to a
  // catalog option: a resolved-but-unlisted id (show strPayMethod or a generic
  // label) or no id at all but a non-empty strPayMethod (e.g. "Visa ****1234").
  const customMethodOption = useMemo(() => {
    if (methodIsKnownOption) return null;
    if (paymentMethodId !== '') {
      return { value: paymentMethodId, label: detail?.strPayMethod || 'Current method' };
    }
    const stored = detail?.strPayMethod?.trim();
    if (stored) return { value: `__stored__${stored}`, label: stored };
    return null;
  }, [methodIsKnownOption, paymentMethodId, detail?.strPayMethod]);

  // Saved billing methods for this patient — used to resolve a transaction's
  // raw paymentMethodToken to the human-readable title (e.g. "Visa ****1234").
  const { data: billingMethods = [] } = useQuery({
    queryKey: ['patient-billing-methods', patientId],
    queryFn: () => listBillingMethods(patientId),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const methodTitleByToken = useMemo(() => {
    const map = new Map<string, BillingMethod>();
    billingMethods.forEach((bm) => {
      if (bm.token) map.set(bm.token, bm);
    });
    return map;
  }, [billingMethods]);

  // The Reason column appears only when an attempt has one, as in the
  // legacy modal (load-payment-transaction-list-authnet.php:412).
  const showDeclineReason = detail?.transactions.some((t) => Boolean(t.declineReason)) ?? false;

  // ─── Patient product types (filtered by this payment) ────────────────────
  const [productData, setProductData] = useState<ProductTypesResponse | null>(null);

  useEffect(() => {
    if (!open || !isEditMode || !patientId) { setProductData(null); return; }
    let cancelled = false;
    getPatientProductTypes(String(patientId)).then((res: ProductTypesResponse) => {
      if (!cancelled) setProductData(res);
    }).catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [open, isEditMode, patientId]);

  const filteredProducts = useMemo(() => {
    if (!productData || !activePaymentId) return { types: [], others: [] };
    return filterProductsByPayment(productData, activePaymentId);
  }, [productData, activePaymentId]);

  const { data: receiptTemplates = [] } = useQuery({
    queryKey: ['payment-receipt-templates'],
    queryFn: listReceiptTemplates,
    enabled: open && isEditMode,
  });

  // The patient shown for an existing payment must be the payment's own
  // patient, not the page we opened from. A gift card purchased by patient A
  // for patient B lives on B's account, so opening the payment from B's gift
  // card would otherwise mislabel it (and link) as B instead of the payer A.
  const displayPatientId = isEditMode ? (detail?.patientID ?? patientId) : patientId;

  // Patient row drives `emailOptIn` gating on the Email Receipt button (legacy
  // parity with `lib/payment-details-charge-gateway.php` line 826-836).
  const { data: patientRecord } = useQuery({
    queryKey: ['patient-record', displayPatientId],
    queryFn: () => getPatient(displayPatientId),
    enabled: open && displayPatientId > 0,
    staleTime: 5 * 60 * 1000,
  });

  const { data: catalogueData } = useQuery({
    queryKey: ['payment-member-check', patientId],
    queryFn: () => appointmentsService.getAppointmentProductCatalogue(null, patientId),
    enabled: open && !!patientId,
    staleTime: 2 * 60_000,
  });
  const resolvedIsMember = catalogueData?.isMember ?? false;
  const memberStatus: string | null = catalogueData?.memberStatus ?? null;
  const showAsMember = resolvedIsMember || memberStatus === 'frozen' || memberStatus === 'declined';

  const { data: facilities = [] } = useQuery({
    queryKey: ['patient-facilities'],
    queryFn: listPatientFacilities,
    staleTime: 5 * 60 * 1000,
  });

  // Discounts, so a saved package payment can show the exact named discount
  // per package (parity with product line items).
  const { data: discounts = [] } = useQuery({
    queryKey: ['payment-discounts'],
    queryFn: () => listDiscounts(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch the receipt PDF on-demand and render via blob URL. Only fires when
  // the receipt panel is open and a template is selected (preview is a real
  // PDF now — different from the old HTML preview).
  const previewQueryEnabled =
    open && isEditMode && panel?.kind === 'receipt' && selectedTemplateId !== null;
  const { data: previewPdfBlob } = useQuery({
    queryKey: ['payment-receipt-preview', activePaymentId, selectedTemplateId],
    queryFn: () => previewReceiptPdfBlob(activePaymentId as number, selectedTemplateId as number),
    enabled: previewQueryEnabled,
  });

  // Mint the object URL locally from the cached Blob, one per mount, and revoke
  // it on unmount / template change. The query must cache the Blob rather than
  // the URL: a URL is dead once revoked, so re-opening the same payment's
  // receipt inside the cache window handed the iframe a revoked URL and it
  // rendered blank (it only "fixed itself" once the entry went stale and
  // refetched).
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!previewPdfBlob) {
      setPreviewBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(previewPdfBlob);
    setPreviewBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [previewPdfBlob]);

  // Auto-select first receipt template when the receipt panel is opened
  useEffect(() => {
    if (panel?.kind !== 'receipt') return;
    if (selectedTemplateId !== null) return;
    if (receiptTemplates.length > 0) {
      setSelectedTemplateId(receiptTemplates[0].templateId);
    }
  }, [panel?.kind, receiptTemplates, selectedTemplateId]);

  // ─── Reset form when modal opens ──────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    // Create mode: reset everything to empty defaults.
    if (!isEditMode) {
      setAmount('');
      setDate(todayInTz(userTz));
      setPayType('Payment');
      setPaymentMethodId('');
      setReferenceNumber('');
      setMemo('');
      setPlanId('');
      setQuoteId('');
      setFacilityId('');
      setGiftCards([]);
      setAmountFromProducts(false);
    }
    // Both modes: drop the previous payment's lines. In edit mode
    // PaymentProductsSection remounts with the modal and re-seeds them, so this
    // only matters when it does not render (package/membership payments) — where
    // stale lines would otherwise be saved onto this payment.
    setProductLines([]);
    // Honor initialPanel only in edit mode — the receipt panel needs a
    // persisted paymentId to preview/email against.
    setPanel(isEditMode && initialPanel === 'receipt' ? { kind: 'receipt' } : null);
    setError(null);
    setSelectedTemplateId(null);
    setShowRetry(false);
    setRetryToken('new');
    setRetrySelectedMethod(null);
    setRetryMerchantId(null);
    setRetryResult(null);
  }, [open, isEditMode, paymentId, initialPanel, userTz]);

  // ─── Hydrate form from loaded detail (edit mode) ──────────────────────────
  useEffect(() => {
    if (!open || !isEditMode || !detail) return;
    // curAmount already includes shipping, but the save contract expects the
    // PRE-shipping base (the server re-adds it from payments_shipping). Strip it
    // back out here or re-saving an unchanged payment would charge shipping twice.
    const curAmountNum = Number(detail.curAmount ?? 0);
    const baseAmount = Number.isFinite(curAmountNum)
      ? Math.max(0, curAmountNum - (detail.shippingTotal ?? 0))
      : 0;
    setAmount(detail.curAmount == null ? '' : baseAmount.toFixed(2));
    setDate(detail.dtmDate ? detail.dtmDate.slice(0, 10) : todayInTz(userTz));
    setPayType((detail.strPayType ?? 'Payment') as string);
    // Pre-select the dropdown. Older records may not have paymentMethodID set
    // even though strPayMethod (e.g. "American Express") is — match by name in
    // that case so the user sees the actual method instead of "Select One...".
    let resolvedMethodId = detail.paymentMethodID ? String(detail.paymentMethodID) : '';
    if (!resolvedMethodId && detail.strPayMethod && paymentMethodOptions?.length) {
      const target = detail.strPayMethod.trim().toLowerCase();
      const match = paymentMethodOptions.find(
        (opt) => opt.name.trim().toLowerCase() === target,
      );
      if (match) resolvedMethodId = String(match.paymentMethodID);
    }
    setPaymentMethodId(resolvedMethodId);
    setReferenceNumber(detail.strRefNumber ?? '');
    setMemo(detail.strMemo ?? '');
    setPlanId(detail.planID ? String(detail.planID) : '');
    setQuoteId(detail.quoteID ? String(detail.quoteID) : '');
    setFacilityId(detail.facilityID ? String(detail.facilityID) : '');
    setGiftCards(mapDetailGiftCardsToApplied(detail));
    // Products are NOT cleared here: PaymentProductsSection owns them via
    // initialLines. This effect re-runs on every detail refetch (a retry, void,
    // refund), and React Query hands back the SAME `materials` array when the
    // lines did not change, so the section never re-emits them. Clearing here
    // left productLines empty while the lines still showed, and Save Changes
    // then sent no items (SCRUM-2133). The open/payment reset above clears them.
    setAmountFromProducts(false);
  }, [open, isEditMode, detail, paymentMethodOptions, userTz]);

  // ─── Product total drives Amount (same rule as ChargePaymentModal) ───────
  const productsTotal = useMemo(
    () => productLines.reduce((sum, line) => sum + computeLineTotal(line, discounts), 0),
    [productLines, discounts],
  );

  useEffect(() => {
    if (isEditMode) return;
    if (productLines.length > 0) {
      setAmount(productsTotal.toFixed(2));
      setAmountFromProducts(true);
    } else if (amountFromProducts) {
      setAmount('');
      setAmountFromProducts(false);
    }
  }, [isEditMode, productsTotal, productLines.length, amountFromProducts]);

  // ─── Shipping: charged once per distinct fee, on top of the product base ───
  // `amount` above deliberately stays the PRE-shipping base. The backend
  // resolves each fee's dollars from `shipping_fees` and folds them into
  // curAmount, so adding shipping into `amount` here would double-charge it.
  const shipping = useShippingFees({
    productLines,
    existingFees: isEditMode ? detail?.shipping : undefined,
    resetKey: open ? (paymentId ?? 'new') : 'closed',
  });

  // ─── Legacy disable rule: type/amount/date/method/reference locked when editing ─
  const lockBasics = isEditMode;

  // ─── Mutations ────────────────────────────────────────────────────────────
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['patient-payments', patientId] });
    // Every write here (save, void, un-void, refund, retry) moves the patient's
    // lifetime totals: the Payments tab's Total Payments line and the Total
    // Spend card. Refreshed here rather than left to onSaved, because void,
    // un-void and refund never call onSaved, and a host may use onSaved to
    // close the modal (NotificationModalHost).
    queryClient.invalidateQueries({ queryKey: ['patient-payment-stats', patientId] });
    queryClient.invalidateQueries({ queryKey: ['patient-sidebar-stats', patientId] });
    if (activePaymentId) {
      queryClient.invalidateQueries({ queryKey: ['payment-detail', activePaymentId] });
    }
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const items = productLines.map((line) => ({
        paymentMaterialID: line.paymentMaterialID,
        itemId: line.itemId,
        // Preserve the true per-line name on edit (integration checkout lines
        // share one generic catalog item per module) — without this, editing
        // an Axiom/GetLabs/etc. payment here would collapse its line names
        // back to the module's generic catalog name.
        name: line.name || undefined,
        description: line.description || undefined,
        price: line.price,
        quantity: line.quantity,
        taxable: line.taxable,
        salesTax: line.salesTax,
        discount1Id: line.discount1Id ?? undefined,
        discount2Id: line.discount2Id ?? undefined,
        manualDiscount1: line.manualDiscount1,
        manualDiscount2: line.manualDiscount2,
      }));

      const loanPayload = isEditMode && detail
        ? detail.loans.map((loan) => ({
          loanId: loan.loanId,
          paymentLoanID: loan.paymentLoanID,
        }))
        : [];

      return createPatientPayment(patientId, {
        paymentID: isEditMode ? (paymentId as number) : undefined,
        // The field holds the shipping-inclusive total, but the wire contract is
        // the PRE-shipping base: the server re-resolves each fee from the account's
        // setup and adds it back, so sending the inclusive figure would charge
        // shipping twice. Clamped, since a manual amount can be below shipping.
        amount: Number(amount),
        date,
        method: resolveSaveMethodName(selectedMethod?.name, isEditMode, detail?.strPayMethod),
        strPayType: payType,
        memo: memo.trim() || undefined,
        referenceNumber: referenceNumber.trim() || undefined,
        paymentMethodID: Number(paymentMethodId) || undefined,
        planID: Number(planId) || undefined,
        quoteID: Number(quoteId) || undefined,
        facilityID: Number(facilityId) || undefined,
        giftCards: giftCards
          .map((card) => ({
            giftCardId: card.giftCard.giftCardId ?? card.giftCard.paymentsCreditID,
            amount: card.amount,
          }))
          .filter((card) => Number.isFinite(card.giftCardId) && card.giftCardId > 0 && card.amount > 0),
        loans: loanPayload,
        // IDs only — the server owns the dollar amounts. See useShippingFees.
        // Always sent, including as an empty array: the server reads an ABSENT
        // key as "caller has no opinion" and preserves existing rows, so an
        // omitted-when-empty payload would make removing the last fee a no-op.
        shipping: shipping.payload,
        items: items.length > 0 ? items : undefined,
        // NB: chargeAmount / gatewayTransactionId deliberately omitted — this
        // is the manual record pipeline, never the gateway charge.
      });
    },
    onSuccess: () => {
      refresh();
      onSaved();
      onClose();
    },
    onError: (err) => {
      setError(getErrorMessage(err, 'Failed to save payment.'));
    },
  });

  const voidMutation = useMutation({
    mutationFn: (id: number) => voidPayment(id),
    onSuccess: refresh,
    // A failed void says whether anything changed; when only the gift card
    // give-back failed the payment is voided, so it is re-read either way.
    onError: (err) => {
      refresh();
      setError(getErrorMessage(err, 'Failed to void this payment.'));
    },
  });
  // Per-row Void on a single PTA. Legacy parity:
  // update_payment_transaction_authnet.php:171-241 — tries Forward cancel,
  // falls back to FULL refund if cancel fails (charge already settled),
  // then flips the parent payments.void=1. Distinct from `voidMutation`
  // above, which is wired to the toolbar Void Payment button and stays
  // on the per-payment local-flip endpoint.
  const voidTransactionMutation = useMutation({
    mutationFn: (transactionId: number) => voidPaymentTransaction(transactionId),
    onSuccess: refresh,
    onError: (err) => {
      // GIFT_CARD_RESTORE_FAILED: the void went through, only the gift card
      // give-back failed, so the payment is re-read.
      refresh();
      setError(getErrorMessage(err, 'Failed to void this transaction.'));
    },
  });
  const unvoidMutation = useMutation({
    mutationFn: (id: number) => unvoidPayment(id),
    onSuccess: refresh,
    // Refused when a gift card the void gave back has been spent since; the
    // payment stays voided and the message says which card.
    onError: (err) => setError(getErrorMessage(err, 'Failed to un-void this payment.')),
  });
  const refundMutation = useMutation({
    mutationFn: (params: { transactionId: number; amount?: number; reason?: string }) =>
      refundPaymentTransaction(params.transactionId, { amount: params.amount, reason: params.reason }),
    onSuccess: () => {
      setPanel(null);
      refresh();
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to refund this transaction.')),
  });
  // ─── Retry (declined) charge ──────────────────────────────────────────────
  // Charge the merchant the dropdown shows (seeded from the declined
  // transaction's merchant). The selected card's stored merchant is deliberately
  // NOT consulted: a Forward card can be charged under any merchant, and letting
  // it win used to send the charge to a merchant other than the one on screen.
  const resolvedRetryMerchantId =
    retryMerchantId ??
    retryableTransaction?.accountAuthnetID ??
    null;

  const retryMutation = useMutation({
    mutationFn: () => {
      if (!activePaymentId) throw new Error('Missing payment.');
      if (!resolvedRetryMerchantId) throw new Error('Select a merchant account.');
      if (!retryToken || retryToken === 'new' || !retrySelectedMethod) {
        throw new Error('Select a saved card or complete the new card entry first.');
      }
      return chargeForwardPayment(activePaymentId, {
        merchant_id: resolvedRetryMerchantId,
        payment_method_id: retrySelectedMethod.token,
        amount: detail?.curAmount ?? 0,
        credit: 0,
        patient_id: patientId,
      });
    },
    onSuccess: (result) => {
      if (result.success) {
        setRetryResult({ kind: 'success', message: 'Payment retried successfully.' });
        setShowRetry(false);
        refresh();
        onSaved();
      } else {
        setRetryResult({
          kind: 'declined',
          message: result.message || 'Transaction error: Payment was declined.',
        });
        refresh();
      }
    },
    onError: (err) => {
      setError(getErrorMessage(err, 'Failed to retry payment.'));
    },
  });

  // A retry that captures money for nothing leaves staff a paid payment with no
  // product to disburse (SCRUM-2133). Warn, but let them go ahead: deposits and
  // plan balance payments legitimately carry no product.
  const hasAttachedProduct = !!detail
    && (detail.materials.length > 0 || detail.packages.length > 0 || detail.memberships.length > 0);
  const handleRetryClick = () => {
    if (!hasAttachedProduct && !confirm(NO_PRODUCT_RETRY_CONFIRM)) return;
    retryMutation.mutate();
  };

  const handleRetryMerchantChange = (id: number, _m: Merchant) => {
    setRetryMerchantId(id);
  };

  const handleRetrySourceChange = (
    token: string,
    method: BillingMethod | null,
    _terminal: PaymentTerminal | null = null,
  ) => {
    setRetryToken(token);
    setRetrySelectedMethod(method);
    setRetryResult(null);
  };

  const handleNewRetrySourceSaved = (method: BillingMethod) => {
    setRetryToken(method.token);
    setRetrySelectedMethod(method);
    setRetryResult(null);
  };

  const emailMutation = useMutation({
    mutationFn: (payload: {
      receiptTemplateId: number;
      subject?: string;
    }) => emailReceipt(activePaymentId as number, payload),
    onSuccess: () => setPanel(null),
    onError: (err) => setError(getErrorMessage(err, 'Failed to send receipt.')),
  });

  // Refs for sign panel canvases (kept here so the submit handler can read them).
  const patientSigRef = useRef<SignaturePadFieldHandle | null>(null);
  const witnessSigRef = useRef<SignaturePadFieldHandle | null>(null);

  const signMutation = useMutation({
    mutationFn: (payload: {
      receiptTemplateId: number;
      signatureData: string;
      witnessSignatureData?: string;
      signedByName?: string;
    }) => signReceiptPdfUrl(activePaymentId as number, payload),
    onSuccess: (signedPdfUrl) => {
      setPanel((current) =>
        current?.kind === 'sign' ? { ...current, signedPdfUrl } : current,
      );
      refresh();
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to sign receipt.')),
  });

  const directLinkMutation = useMutation({
    mutationFn: (templateId: number) =>
      receiptDirectLink(activePaymentId as number, templateId),
    onError: (err) => setError(getErrorMessage(err, 'Failed to generate direct link.')),
  });

  // ─── Create-mode readiness ──────────────────────────────────────────────
  const createParsedAmount = Number(amount) || 0;
  const createGiftCardTotal = giftCards.reduce((sum, gc) => sum + gc.amount, 0);
  const createNetAmount = Math.max(0, createParsedAmount - createGiftCardTotal);

  const canSubmitCreate = !isEditMode
    && createParsedAmount > 0
    && payType.trim() !== '' && payType !== 'Not Specified'
    && paymentMethodId !== ''
    && date !== ''
    && facilityId !== '';

  // ─── Submit validation ────────────────────────────────────────────────────
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!isEditMode) {
      const parsedAmount = Number(amount);
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        setError('Enter a valid amount.');
        return;
      }
      if (!payType.trim() || payType === 'Not Specified') {
        setError('Enter a payment type.');
        return;
      }
      if (!selectedMethod) {
        setError('Enter a payment method.');
        return;
      }
      if (!date) {
        setError('Enter a payment date.');
        return;
      }
      if (!facilityId) {
        setError('Select a facility.');
        return;
      }
    }

    saveMutation.mutate();
  }

  // ─── Resolve patient name ─────────────────────────────────────────────────
  // In edit mode the name follows the payment's own patient (patientRecord is
  // fetched by displayPatientId); the patientName prop only labels create mode.
  const patientRecordName = patientRecord
    ? [patientRecord.firstName, patientRecord.lastName].filter(Boolean).join(' ')
    : null;
  const resolvedPatientName = isEditMode ? patientRecordName : (patientName || patientRecordName);

  const resolvedFacilityName = useMemo(() => {
    if (!detail?.facilityID) return null;
    return facilities.find((f) => f.facilityId === detail.facilityID)?.facilityName ?? null;
  }, [detail?.facilityID, facilities]);

  // The merchant account that actually took the money: the newest successful
  // charge (transactions arrive newest-first). Stored per attempt, independently
  // of the payment's facility, so the two can name different locations.
  const chargedMerchantTitle = useMemo(() => {
    const charged = (detail?.transactions ?? []).find(
      (t) => isSuccessStatus(t.status) && !(t.status ?? '').toLowerCase().includes('refund'),
    );
    return charged?.merchantTitle ?? null;
  }, [detail?.transactions]);

  const giftCardTotal = useMemo(() => {
    if (!detail?.giftCards?.length) return 0;
    return detail.giftCards.reduce((sum, gc) => sum + (gc.amount ?? 0), 0);
  }, [detail?.giftCards]);

  // Gross (pre-tender) and card-charged figures come from the server, which is
  // the only place that knows whether this row's curAmount is net or gross of
  // the gift card — the two write paths disagree. Fall back to the old net
  // assumption so a stale API response still renders something sane.
  const grossAmount = detail?.grossAmount ?? ((detail?.curAmount ?? 0) + giftCardTotal);
  const cardChargedAmount = detail?.cardChargedAmount ?? (detail?.curAmount ?? 0);
  // What was sold, as opposed to what was tendered — the two differ whenever a
  // discount or a credit is in play.
  const productTotal = detail?.productTotal ?? grossAmount;

  if (!open) return null;

  const modalTitle = isEditMode && detail ? (
    <div className="flex items-center gap-3">
      <div className="min-w-0">
        {resolvedPatientName && (
          <Link
            to={`/patients/${displayPatientId}`}
            className="text-xs font-medium text-blue-600 hover:underline"
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            {resolvedPatientName}
          </Link>
        )}
        <div className="truncate text-base font-bold text-slate-900">
          Payment #{detail.paymentID}
        </div>
      </div>
      {statusDisplay && (statusDisplay.voided || statusDisplay.declined) && (
        <span className="ml-auto mr-4 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold bg-red-50 text-red-600">
          {statusDisplay.label}
        </span>
      )}
    </div>
  ) : isEditMode ? 'Payment Details' : (
    <div>
      <h2 className="text-base font-semibold text-slate-900">Record Payment</h2>
      <p className="text-sm font-normal text-slate-500">
        {resolvedPatientName || `Patient #${patientId}`}
        {showAsMember && (
          <span className="ml-2 inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">Member</span>
        )}
        {memberStatus === 'frozen' && (
          <span className="ml-1 inline-flex items-center rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700">Frozen</span>
        )}
        {memberStatus === 'declined' && (
          <span className="ml-1 inline-flex items-center rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-700">Past Due</span>
        )}
        {resolvedIsMember && (
          <span className="ml-1 text-xs text-emerald-600">Prices reflect membership rates</span>
        )}
      </p>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Modal
      isOpen={open}
      onClose={saveMutation.isPending ? () => {} : onClose}
      title={modalTitle}
      size="xxl"
      footer={(
        <>
          {isEditMode ? (
            <Button size="sm" variant="secondary" onClick={() => setPanel({ kind: 'receipt' })}>
              Receipt
            </Button>
          ) : null}
          {isEditMode && !isVoid && !isDeclined && primaryToolbarAction ? (
            <Button
              variant="danger"
              onClick={() => {
                if (!detail) return;
                // Settled gateway charge → open the Refund dialog (Full /
                // Partial + amount), mirroring legacy patient_details.php
                // updateTransaction()'s 'refund' branch. Anything else is a
                // gateway void/cancel.
                if (primaryToolbarAction === 'refund' && latestCapturedPta) {
                  setPanel({
                    kind: 'refund',
                    transactionId: latestCapturedPta.paymentTransactionAuthnetID,
                    originalAmount: Math.abs(latestCapturedPta.amount ?? 0),
                  });
                  return;
                }
                if (!confirm(toolbarVoidConfirm)) return;
                if (latestCapturedPta) {
                  voidTransactionMutation.mutate(latestCapturedPta.paymentTransactionAuthnetID);
                } else {
                  voidMutation.mutate(detail.paymentID);
                }
              }}
              loading={voidMutation.isPending || voidTransactionMutation.isPending}
            >
              {toolbarVoidLabel}
            </Button>
          ) : null}
          {isEditMode && isVoid ? (
            <Button
              variant="secondary"
              onClick={() => detail && unvoidMutation.mutate(detail.paymentID)}
              loading={unvoidMutation.isPending}
            >
              Un-Void Payment
            </Button>
          ) : null}
          {!isEditMode && (
            <div className="flex items-center gap-3">
              {createGiftCardTotal > 0 ? (
                <>
                  <span className="text-sm text-slate-400 line-through">${createParsedAmount.toFixed(2)}</span>
                  <span className="flex items-center gap-1 text-xs text-emerald-600">
                    <Gift className="h-3.5 w-3.5" />
                    -${createGiftCardTotal.toFixed(2)}
                  </span>
                  <span className="text-lg font-bold text-slate-900">${createNetAmount.toFixed(2)}</span>
                </>
              ) : createParsedAmount > 0 ? (
                <span className="text-lg font-bold text-slate-900">${createParsedAmount.toFixed(2)}</span>
              ) : null}
            </div>
          )}
          <Button
            type="submit"
            form="record-payment-form"
            loading={saveMutation.isPending}
            disabled={isEditMode ? isVoid : !canSubmitCreate}
          >
            {isEditMode ? 'Save Changes' : 'Record Payment'}
          </Button>
        </>
      )}
    >
      {isEditMode && detailLoading ? (
        <div className="py-10 text-center text-sm text-slate-500">Loading payment details…</div>
      ) : isEditMode && detailError ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {getErrorMessage(detailError, 'Failed to load payment details.')}
        </div>
      ) : (
        <form id="record-payment-form" onSubmit={handleSubmit} className="space-y-4">
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {isEditMode && isVoid ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              This payment has been voided{detail?.voidDate ? ` on ${formatDate(detail.voidDate, userTz)}` : ''}{detail?.voidUserName ? ` by ${detail.voidUserName}` : ''}.
            </div>
          ) : null}

          {isEditMode && detail?.strPayType?.toLowerCase() === 'refund' ? (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              This is a refund{detail?.entryDate ? ` processed on ${formatDate(detail.entryDate, userTz)}` : ''}{detail?.enteringUserName ? ` by ${detail.enteringUserName}` : ''}.
            </div>
          ) : null}

          {/* ─── Payment Title Card (edit mode) ─────────────────────────── */}
          {isEditMode && detail ? (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  {giftCardTotal > 0 ? (
                    <>
                      {/* Headline is what the products cost; the two lines under
                          it are how it was paid for. */}
                      <p className="text-xs font-medium text-slate-500">Product Total</p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">
                        {formatCurrency(productTotal)}
                      </p>
                      <div className="mt-2 flex items-center gap-1 text-xs text-slate-600">
                        <Gift className="h-3.5 w-3.5 text-purple-500" />
                        <span>{formatCurrency(giftCardTotal)} gift card</span>
                      </div>
                      <div className="mt-1 flex items-center gap-1 text-xs text-slate-600">
                        <CreditCard className="h-3.5 w-3.5 text-slate-400" />
                        <span>{formatCurrency(cardChargedAmount)} charged</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-medium text-slate-500">Amount</p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">
                        {formatCurrency(detail.curAmount)}
                      </p>
                    </>
                  )}
                  {resolvedFacilityName ? (
                    <p className="mt-2 text-xs text-slate-500">{resolvedFacilityName}</p>
                  ) : null}
                  {chargedMerchantTitle ? (
                    <p className="mt-1 text-xs text-slate-500">Merchant: {chargedMerchantTitle}</p>
                  ) : null}
                </div>
                <div className="flex-shrink-0 text-right">
                  {/* Date and time both come from entryDate — when the payment was
                      actually taken — so the two lines are one consistent
                      conversion into the viewer's zone. This used to read the
                      dtmDate business-date column, which disagreed with the time
                      beneath it whenever the two diverged. */}
                  <div className="text-sm text-slate-800">
                    {formatPaymentDate(detail, userTz, { month: 'long', day: 'numeric', year: 'numeric' })}
                  </div>
                  {detail.entryDate ? (
                    <div className="text-xs text-slate-500">
                      {toLocaleTimeStringInTz(detail.entryDate, 'en-US', { hour: 'numeric', minute: '2-digit', timeZone: userTz, timeZoneName: 'short' })}
                    </div>
                  ) : null}
                  <div className="mt-1 text-xs text-slate-500">
                    {cardChargedAmount === 0 && giftCardTotal > 0
                      ? 'Gift Card'
                      : grossAmount === 0
                        ? 'Comp'
                        : `${detail.strPayType ?? ''}${detail.strPayMethod ? ` · ${detail.strPayMethod}` : ''}`}
                  </div>
                  {detail.strRefNumber ? (
                    <div className="mt-1 text-xs text-slate-500">
                      Ref: {detail.strRefNumber}
                    </div>
                  ) : null}
                  {detail.planID && detail.planID !== 0 ? (
                    <div className="mt-1">
                      <Link to={`/plans/${detail.planID}`} className="text-xs text-blue-600 hover:underline" target="_blank">
                        Plan #{detail.planID}
                      </Link>
                    </div>
                  ) : null}
                  {detail.promoCode ? (
                    <div className="mt-1.5 flex items-center justify-end gap-1 text-xs text-purple-600">
                      <Tag className="h-3 w-3" />
                      <span className="font-mono font-medium">{detail.promoCode.code}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          {/* ─── Transactions (edit mode, top of modal) ──────────────────── */}
          {isEditMode && detail && detail.transactions.length > 0 ? (
            <section className="rounded-lg border border-slate-200">
              <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Transactions</h3>
                <span className="text-xs text-slate-500">
                  {detail.transactions.length} {detail.transactions.length === 1 ? 'record' : 'records'}
                </span>
              </header>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-100 text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">Transaction ID</th>
                      <th className="px-4 py-2">Method</th>
                      <th className="px-4 py-2">Merchant</th>
                      <th className="px-4 py-2">Status</th>
                      {showDeclineReason ? <th className="px-4 py-2">Reason</th> : null}
                      <th className="px-4 py-2 text-right">Amount</th>
                      <th className="px-4 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(() => {
                      let actionShown = false;
                      return detail.transactions.map((transaction) => {
                        const billing = transaction.paymentMethodToken
                          ? methodTitleByToken.get(transaction.paymentMethodToken) ?? null
                          : null;
                        const methodLabel = billing
                          ? billing.title ||
                            (billing.lastFour ? `•••• ${billing.lastFour}` : '—')
                          : transaction.paymentMethodToken
                            ? 'Saved card'
                            : '—';
                        const rawStatus = (transaction.status ?? '').trim();
                        const statusLower = rawStatus.toLowerCase();
                        const statusVariant: 'success' | 'warning' | 'error' | 'neutral' =
                          isSuccessStatus(rawStatus)
                            ? 'success'
                            : statusLower.includes('refund')
                              ? 'warning'
                              : statusLower === 'declined' || statusLower === 'failed' || statusLower === 'voided'
                                ? 'error'
                                : 'neutral';

                        let action = actionShown
                          ? null
                          : resolveTransactionAction(rawStatus, transaction.transactionID);

                        const actionLabel =
                          action === 'void' ? 'Void'
                            : action === 'refund' ? 'Refund'
                              : action === 'retry' ? 'Retry'
                                : null;

                        if (action) actionShown = true;

                        const handleAction = () => {
                          if (!action) return;
                          if (action === 'void') {
                            // Per-row Void → gateway cancel-then-refund-fallback
                            // (legacy parity: update_payment_transaction_authnet.php:171-241).
                            // Distinct from the toolbar Void Payment button at line 562
                            // which keeps using the per-payment local-only endpoint.
                            if (confirm('Void this transaction at the gateway?')) {
                              voidTransactionMutation.mutate(transaction.paymentTransactionAuthnetID);
                            }
                          } else if (action === 'refund') {
                            // Open the Refund dialog (Full / Partial + amount)
                            // instead of firing a full refund immediately —
                            // legacy parity with patient_details.php
                            // updateTransaction()'s 'refund' branch.
                            setPanel({
                              kind: 'refund',
                              transactionId: transaction.paymentTransactionAuthnetID,
                              originalAmount: Math.abs(transaction.amount ?? 0),
                            });
                          } else if (action === 'retry') {
                            // Legacy parity: open the card chooser so the user can
                            // retry with the same, a different, or a brand-new card.
                            setRetryResult(null);
                            setRetryMerchantId(
                              (current) => current ?? transaction.accountAuthnetID ?? null,
                            );
                            setShowRetry(true);
                          }
                        };

                        return (
                          <tr key={transaction.paymentTransactionAuthnetID} className="hover:bg-slate-50">
                            <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                              {formatDate(transaction.createDate, userTz)}
                            </td>
                            <td className="px-4 py-2 font-mono text-xs text-slate-500" title={transaction.transactionID ?? ''}>
                              {transaction.transactionID ?? '—'}
                            </td>
                            <td className="px-4 py-2 text-slate-700">{methodLabel}</td>
                            <td className="px-4 py-2 text-slate-700">{transaction.merchantTitle ?? '—'}</td>
                            <td className="px-4 py-2">
                              {rawStatus ? (
                                <Badge variant={statusVariant}>{formatStatusText(rawStatus)}</Badge>
                              ) : '—'}
                            </td>
                            {showDeclineReason ? (
                              <td className="min-w-[12rem] px-4 py-2 text-slate-700">
                                {transaction.declineReason ?? ''}
                              </td>
                            ) : null}
                            <td className="px-4 py-2 text-right font-medium text-slate-900 whitespace-nowrap">
                              {formatCurrency(transaction.amount)}
                            </td>
                            <td className="px-4 py-2 text-right">
                              {actionLabel ? (
                                <button
                                  type="button"
                                  className="text-sm font-bold text-blue-600 underline hover:text-blue-800 disabled:opacity-50"
                                  disabled={voidTransactionMutation.isPending || refundMutation.isPending}
                                  onClick={handleAction}
                                >
                                  {actionLabel}
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {/* ─── Retry declined payment (edit mode) ───────────────────────── */}
          {/* Single entry point: revealed by the Transactions row "Retry" link.
              No separate button here — that would duplicate the row action. */}
          {isEditMode && detail && canRetry && (showRetry || retryResult) ? (
            <section className="rounded-lg border border-slate-200">
              <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Retry Payment</h3>
              </header>
              <div className="space-y-4 p-4">
                {retryResult ? (
                  <div
                    className={`rounded-md px-4 py-3 text-sm ${
                      retryResult.kind === 'success'
                        ? 'border border-green-200 bg-green-50 text-green-800'
                        : 'border border-red-200 bg-red-50 text-red-800'
                    }`}
                  >
                    {retryResult.message}
                  </div>
                ) : null}

                {showRetry ? (
                  <>
                    <p className="text-xs text-slate-500">
                      Select an existing card or add a new one, then retry the{' '}
                      {formatCurrency(detail.curAmount)} charge.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <PaymentSourceSelector
                        patientId={patientId}
                        value={retryToken}
                        merchantId={resolvedRetryMerchantId}
                        onChange={handleRetrySourceChange}
                      />
                      <MerchantSelector
                        value={retryMerchantId}
                        onChange={handleRetryMerchantChange}
                        preferredFacilityId={detail.facilityID ?? null}
                      />
                    </div>

                    {retryToken === 'new' ? (
                      <ForwardBillingMethodCapture
                        active
                        patientId={patientId}
                        merchantId={resolvedRetryMerchantId}
                        onSaved={handleNewRetrySourceSaved}
                      />
                    ) : null}

                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowRetry(false)}
                        disabled={retryMutation.isPending}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleRetryClick}
                        loading={retryMutation.isPending}
                        disabled={retryToken === 'new' || !retrySelectedMethod}
                      >
                        Retry {formatCurrency(detail.curAmount)}
                      </Button>
                    </div>
                  </>
                ) : null}
              </div>
            </section>
          ) : null}

          {/* ─── Products line items + shipping (create mode) ─────────────────
               Shipping is attached directly beneath the line items and their
               discount and total rows, never among them: it is not a product and
               is charged once per distinct fee. Staff read the cost off this
               block, so a fee shown only lower down left them unable to see why
               the charged figure differed. The Amount field stays the product
               base; the server adds shipping on save, which is why Total here can
               exceed Amount. */}
          {!isEditMode && (
            <div>
              <PaymentProductsSection
                onChange={setProductLines}
                isMember={resolvedIsMember}
                showAsMember={showAsMember}
              />
              {(shipping.appliedFees.length > 0 || shipping.availableFees.length > 0) && (
                <div className="rounded-b-md border border-t-0 border-slate-200">
                  <PaymentShippingSection
                    appliedFees={shipping.appliedFees}
                    shippingTotal={shipping.shippingTotal}
                    availableFees={shipping.availableFees}
                    onRemove={shipping.removeFee}
                    onAdd={shipping.addFee}
                    productsTotal={productsTotal}
                  />
                </div>
              )}
            </div>
          )}

          {/* ─── Itemization (above Products / Payment Details) ────────────── */}
          {isEditMode && detail && detail.packages.length > 0 ? (
            detail.packages.map((pkg) => {
              const subtotal = pkg.templatePrice || pkg.packageTotal;
              const chargeAmount = pkg.chargeAmount;
              const creditApplied = pkg.credit;
              // This package's share of the gift card, not the payment-level
              // total — otherwise a multi-package payment deducts the whole
              // card inside every box and none of them reconcile.
              const pkgGiftCard = pkg.giftCardApplied;
              const pkgDiscount = subtotal - chargeAmount - pkgGiftCard - creditApplied;
              const hasDiscount = pkgDiscount > 0.005;
              // Exact named discounts applied to this package (parity with product lines).
              const appliedDiscounts = [
                { id: pkg.discount1Id, amount: pkg.manualDiscount1 },
                { id: pkg.discount2Id, amount: pkg.manualDiscount2 },
              ]
                .filter((d) => d.id && d.amount > 0.005)
                .map((d) => {
                  const disc = discounts.find((x: PaymentDiscount) => x.paymentDiscountID === d.id);
                  const pct = disc?.discountType === 2 ? `(${disc.discountAmount}%) ` : '';
                  return { label: `${pct}${disc?.title ?? `Discount #${d.id}`}`, amount: d.amount };
                });
              return (
                <CollapsibleSection key={pkg.packageId} title={pkg.title || `Package #${pkg.packageId}`} defaultOpen>
                  <div className="space-y-4 p-4">
                    {pkg.items.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-medium text-slate-400">Package Contents</p>
                        <div className="space-y-1">
                          {pkg.items.map((item, idx) => (
                            <div key={idx} className="flex items-baseline gap-3 text-sm">
                              <span className="w-6 text-right font-semibold text-slate-900 tabular-nums">{item.quantity}</span>
                              <span className="text-slate-700">{item.itemName}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Subtotal (Package Price):</span>
                        <span className="text-slate-800">{formatCurrency(subtotal)}</span>
                      </div>
                      {appliedDiscounts.length > 0 ? (
                        appliedDiscounts.map((d, i) => (
                          <div key={`disc-${i}`} className="flex justify-between text-amber-700">
                            <span>{d.label}:</span>
                            <span>−{formatCurrency(d.amount)}</span>
                          </div>
                        ))
                      ) : hasDiscount ? (
                        <div className="flex justify-between text-amber-700">
                          <span>Discount:</span>
                          <span>−{formatCurrency(pkgDiscount)}</span>
                        </div>
                      ) : null}
                      {pkgGiftCard > 0 && (
                        <div className="flex justify-between text-purple-700">
                          <span>Gift Card{(detail.giftCards ?? []).length > 1 ? 's' : ''} Applied:</span>
                          <span>−{formatCurrency(pkgGiftCard)}</span>
                        </div>
                      )}
                      {creditApplied > 0 && (
                        <div className="flex justify-between text-blue-700">
                          <span>Credit Applied:</span>
                          <span>−{formatCurrency(creditApplied)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold border-t border-slate-300 pt-1">
                        <span className="text-slate-800">Amount Charged:</span>
                        <span className="text-slate-900">{formatCurrency(chargeAmount)}</span>
                      </div>
                    </div>
                  </div>
                </CollapsibleSection>
              );
            })
          ) : isEditMode && detail && detail.memberships.length > 0 ? (
            detail.memberships.map((mem) => (
              <CollapsibleSection key={mem.enrollmentId} title={mem.title || `Membership #${mem.enrollmentId}`} defaultOpen>
                <div className="space-y-4 p-4">
                  {mem.paymentDate && (
                    <p className="text-sm text-slate-700">
                      Payment for <span className="font-medium">{formatDateOnly(mem.paymentDate)}</span>
                      {mem.paymentUntilDate && (
                        <> to <span className="font-medium">{formatDateOnly(mem.paymentUntilDate)}</span></>
                      )}
                    </p>
                  )}
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Recurring Amount:</span>
                      <span className="text-slate-800">{formatCurrency(mem.recurringAmount)}</span>
                    </div>
                    {/* Only safe to itemize the gift card here when this payment
                        funded a single enrollment; otherwise the payment-level
                        card would be deducted once per membership box. For the
                        multi-enrollment case the header carries it instead. */}
                    {giftCardTotal > 0 && detail.memberships.length === 1 && (
                      <div className="flex justify-between text-purple-700">
                        <span>Gift Card{(detail.giftCards ?? []).length > 1 ? 's' : ''} Applied:</span>
                        <span>−{formatCurrency(giftCardTotal)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t border-slate-300 pt-1">
                      <span className="text-slate-800">Amount Charged:</span>
                      <span className="text-slate-900">
                        {formatCurrency(detail.memberships.length === 1 ? cardChargedAmount : mem.recurringAmount)}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-sm font-medium text-blue-600 hover:underline"
                    onClick={() => setMembershipModalId(mem.enrollmentId)}
                  >
                    View Membership Details
                  </button>
                </div>
              </CollapsibleSection>
            ))
          ) : isEditMode && detail ? (
            <>
              <PaymentProductsSection
                initialLines={detail.materials}
                paymentId={activePaymentId ?? undefined}
                onChange={setProductLines}
                readOnly
              />
              {/* Every line that issued a gift card links to that card's detail
                  modal. A gift card sold online shows its serial on its line
                  until the recipient claims it; after the claim the API leaves
                  the serial out, and the card belongs to the recipient: name
                  them, with a link to their profile, in its place (R22). */}
              {detail.materials.some((m) => (m.giftCardId ?? 0) > 0) && (
                <div className="space-y-1 rounded-b-md border border-t-0 border-slate-200 px-3 py-2 text-sm">
                  {detail.materials
                    .filter((m) => (m.giftCardId ?? 0) > 0)
                    .map((m) => (
                      <div key={m.paymentMaterialID} className="flex flex-wrap items-center gap-1.5 text-slate-600">
                        <Gift className="h-3.5 w-3.5 text-purple-500" />
                        {m.giftCardClaimed ? (
                          <>
                            <span>{m.name || 'Gift Card'}: Claimed by</span>
                            {m.giftCardOwnerPatientId ? (
                              <PatientLink
                                patientId={m.giftCardOwnerPatientId}
                                patientName={m.giftCardOwnerName ?? `Patient #${m.giftCardOwnerPatientId}`}
                                className="font-medium text-blue-600 hover:underline"
                              >
                                {m.giftCardOwnerName ?? `Patient #${m.giftCardOwnerPatientId}`}
                              </PatientLink>
                            ) : (
                              <span className="font-medium text-slate-800">{m.giftCardOwnerName ?? 'the recipient'}</span>
                            )}
                          </>
                        ) : (
                          <span>
                            {m.name || 'Gift Card'}
                            {m.serialNumber ? (
                              <span className="ml-1 font-mono text-slate-700">{m.serialNumber}</span>
                            ) : null}
                          </span>
                        )}
                        <button
                          type="button"
                          className="ml-auto text-xs font-medium text-blue-600 hover:underline"
                          onClick={() => setGiftCardModalId(m.giftCardId ?? null)}
                        >
                          View gift card
                        </button>
                      </div>
                    ))}
                </div>
              )}
              {/* Shipping charged on this payment — snapshotted amounts, so this
                  shows what the patient actually paid even if the fee has since
                  changed. Read-only alongside the read-only line items. */}
              {detail.shipping.length > 0 && (
                <div className="rounded-b-md border border-t-0 border-slate-200">
                  {/* Total is derived from curAmount rather than re-added, so it is
                      what was actually charged even if a fee has changed since. */}
                  <PaymentShippingSection
                    appliedFees={shipping.appliedFees}
                    shippingTotal={shipping.shippingTotal}
                    availableFees={[]}
                    onRemove={shipping.removeFee}
                    onAdd={shipping.addFee}
                    productsTotal={Math.max(
                      0,
                      Number(detail.curAmount ?? 0) - detail.shippingTotal,
                    )}
                    readOnly
                  />
                </div>
              )}
            </>
          ) : null}

          {/* ─── Patient inventory for this payment ───────────────────────── */}
          {isEditMode && detail && detail.packages.length === 0 && detail.memberships.length === 0 && (
            <CollapsibleSection title="Products" defaultOpen>
              {!productData ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  <span className="ml-2 text-xs text-slate-400">Loading products…</span>
                </div>
              ) : filteredProducts.types.length === 0 && filteredProducts.others.length === 0 ? (
                <p className="py-3 text-center text-xs italic text-slate-400">No products for this payment</p>
              ) : (
                <div className="space-y-1 p-2">
                  {filteredProducts.types.map((g) => (
                    <ProductGroupCard
                      key={`pt-${g.productTypeID}`}
                      group={g}
                      listType="product-types"
                      onAction={async () => {}}
                      readOnly
                    />
                  ))}
                  {filteredProducts.others.map((g) => (
                    <ProductGroupCard
                      key={`po-${g.productID ?? g.productTypeID}`}
                      group={g}
                      listType="product-others"
                      onAction={async () => {}}
                      readOnly
                    />
                  ))}
                </div>
              )}
            </CollapsibleSection>
          )}

          {/* ─── Promo Code details ───────────────────────────────────── */}
          {isEditMode && detail?.promoCode ? (
            <section className="rounded-lg border border-purple-200 bg-purple-50/50">
              <header className="flex items-center gap-2 border-b border-purple-200 px-4 py-3">
                <Tag className="h-4 w-4 text-purple-600" />
                <h3 className="text-sm font-semibold text-purple-900">Promo Code</h3>
                <span className="ml-auto font-mono text-sm font-medium text-purple-700">{detail.promoCode.code}</span>
              </header>
              <div className="px-4 py-3 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Title</span>
                  <span className="font-medium text-slate-800">{detail.promoCode.title || '—'}</span>
                </div>
                {detail.promoCode.description ? (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Description</span>
                    <span className="text-slate-700">{detail.promoCode.description}</span>
                  </div>
                ) : null}
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Discount</span>
                  <span className="font-medium text-slate-800">
                    {detail.promoCode.discountTitle}
                    {detail.promoCode.discountType === 1 ? ` ($${detail.promoCode.discountAmount.toFixed(2)} flat)` : ''}
                    {detail.promoCode.discountType === 2 ? ` (${detail.promoCode.discountAmount}%)` : ''}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Applied Discount</span>
                  <span className="font-semibold text-purple-700">−${detail.promoCode.appliedDiscount.toFixed(2)}</span>
                </div>
              </div>
            </section>
          ) : null}

          {/* ─── Payment Details fields ─────────────────────────────────── */}
          {isEditMode ? (
            <CollapsibleSection title="Payment Details">
              <div className="space-y-4 p-4">
                <div className="grid grid-cols-3 gap-4">
                  <label className="space-y-1 text-sm text-slate-700">
                    <span className="font-medium">Type</span>
                    <select
                      value={payType}
                      onChange={(e) => setPayType(e.target.value)}
                      disabled={lockBasics}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 disabled:bg-slate-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="Not Specified">Select One...</option>
                      {MANUAL_PAYMENT_TYPE_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                      {customPayType ? (
                        <option value={customPayType}>{customPayType}</option>
                      ) : null}
                    </select>
                  </label>
                  <label className="space-y-1 text-sm text-slate-700">
                    <span className="font-medium">
                      Amount
                      {amountFromProducts ? (
                        <span className="ml-1 text-xs font-normal text-slate-500">(from products)</span>
                      ) : null}
                    </span>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      disabled={lockBasics || amountFromProducts}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 disabled:bg-slate-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                  <label className="space-y-1 text-sm text-slate-700">
                    <span className="font-medium">Date</span>
                    <DatePicker
                      value={date}
                      onChange={setDate}
                      disabled={lockBasics}
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <label className="space-y-1 text-sm text-slate-700">
                    <span className="font-medium">Method</span>
                    <select
                      value={customMethodOption ? customMethodOption.value : paymentMethodId}
                      onChange={(e) => setPaymentMethodId(e.target.value)}
                      disabled={lockBasics}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 disabled:bg-slate-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select One...</option>
                      {(paymentMethodOptions ?? []).map((option: PaymentMethodOption) => (
                        <option key={option.paymentMethodID} value={option.paymentMethodID}>
                          {option.name}
                        </option>
                      ))}
                      {customMethodOption ? (
                        <option value={customMethodOption.value}>{customMethodOption.label}</option>
                      ) : null}
                    </select>
                  </label>
                  <label className="space-y-1 text-sm text-slate-700">
                    <span className="font-medium">Reference Number</span>
                    <input
                      type="text"
                      value={referenceNumber}
                      onChange={(e) => setReferenceNumber(e.target.value)}
                      disabled={lockBasics || selectedMethod?.isCreditType}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 disabled:bg-slate-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </label>
                </div>
                <EnrollmentLinkageFields
                  patientId={patientId}
                  planId={planId}
                  onPlanIdChange={setPlanId}
                  quoteId={quoteId}
                  onQuoteIdChange={setQuoteId}
                  facilityId={facilityId}
                  onFacilityIdChange={setFacilityId}
                  showPlanQuote={false}
                  facilityRequired
                />
                <GiftCardSelector
                  appliedCards={giftCards}
                  onChange={setGiftCards}
                  maxAmount={Math.max(0, (Number(amount) || 0))}
                  patientId={patientId}
                  readOnly={isEditMode}
                  onViewCard={isEditMode ? setGiftCardModalId : undefined}
                />
              </div>
            </CollapsibleSection>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4">
                <label className="space-y-1 text-sm text-slate-700">
                  <span className="font-medium">Type <span className="text-red-500">*</span></span>
                  <select
                    value={payType}
                    onChange={(e) => setPayType(e.target.value)}
                    disabled={lockBasics}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 disabled:bg-slate-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="Not Specified">Select One...</option>
                    {MANUAL_PAYMENT_TYPE_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1 text-sm text-slate-700">
                  <span className="font-medium">
                    Amount <span className="text-red-500">*</span>
                    {amountFromProducts ? (
                      <span className="ml-1 text-xs font-normal text-slate-500">(from products)</span>
                    ) : null}
                  </span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={lockBasics || amountFromProducts}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 disabled:bg-slate-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </label>
                <label className="space-y-1 text-sm text-slate-700">
                  <span className="font-medium">Date <span className="text-red-500">*</span></span>
                  <DatePicker
                    value={date}
                    onChange={setDate}
                    disabled={lockBasics}
                    required
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="space-y-1 text-sm text-slate-700">
                  <span className="font-medium">Method <span className="text-red-500">*</span></span>
                  <select
                    value={paymentMethodId}
                    onChange={(e) => setPaymentMethodId(e.target.value)}
                    disabled={lockBasics}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 disabled:bg-slate-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select One...</option>
                    {(paymentMethodOptions ?? []).map((option: PaymentMethodOption) => (
                      <option key={option.paymentMethodID} value={option.paymentMethodID}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1 text-sm text-slate-700">
                  <span className="font-medium">Reference Number</span>
                  <input
                    type="text"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    disabled={lockBasics || selectedMethod?.isCreditType}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 disabled:bg-slate-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </label>
              </div>
              <EnrollmentLinkageFields
                patientId={patientId}
                planId={planId}
                onPlanIdChange={setPlanId}
                quoteId={quoteId}
                onQuoteIdChange={setQuoteId}
                facilityId={facilityId}
                onFacilityIdChange={setFacilityId}
                showPlanQuote={false}
                facilityRequired
              />
              <GiftCardSelector
                appliedCards={giftCards}
                onChange={setGiftCards}
                maxAmount={Math.max(0, (Number(amount) || 0))}
                patientId={patientId}
                readOnly={isEditMode}
                onViewCard={isEditMode ? setGiftCardModalId : undefined}
              />
            </>
          )}

          {/* ─── Memo ───────────────────────────────────────────────────── */}
          <label className="space-y-1 text-sm text-slate-700">
            <span className="font-medium">Memo</span>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </label>

          {/* ─── Edit-mode extras ───────────────────────────────────────── */}
          {isEditMode && detail ? (
            <>
              {panel?.kind === 'receipt' ? (
                <ReceiptModal
                  open
                  onClose={() => {
                    setPanel(null);
                    directLinkMutation.reset();
                  }}
                  paymentId={activePaymentId as number}
                  receiptTemplates={receiptTemplates}
                  selectedTemplateId={selectedTemplateId}
                  onSelectTemplate={setSelectedTemplateId}
                  previewBlobUrl={previewBlobUrl}
                  onDirectLink={() =>
                    selectedTemplateId
                      ? directLinkMutation.mutate(selectedTemplateId)
                      : setError('Pick a receipt template first.')
                  }
                  directLink={directLinkMutation.data ?? null}
                  directLinkLoading={directLinkMutation.isPending}
                  showEmailOption={!!patientRecord?.emailOptIn}
                  onSendEmail={(payload) => emailMutation.mutate(payload)}
                  emailSending={emailMutation.isPending}
                />
              ) : null}

              {panel?.kind === 'sign' ? (
                <SignReceiptPanel
                  receiptTemplates={receiptTemplates}
                  selectedTemplateId={selectedTemplateId}
                  onSelectTemplate={setSelectedTemplateId}
                  signerName={panel.signerName}
                  onSignerNameChange={(v) => setPanel({ ...panel, signerName: v })}
                  signedPdfUrl={panel.signedPdfUrl}
                  loading={signMutation.isPending}
                  patientSigRef={patientSigRef}
                  witnessSigRef={witnessSigRef}
                  onSubmit={() => {
                    const receiptTemplateId = selectedTemplateId
                      ?? receiptTemplates[0]?.templateId
                      ?? 0;
                    if (!receiptTemplateId) {
                      setError('Select a receipt template first.');
                      return;
                    }
                    const patientData = patientSigRef.current?.getDataUrl();
                    if (!patientData) {
                      setError('Patient signature is required.');
                      return;
                    }
                    const witnessData = witnessSigRef.current?.getDataUrl() ?? null;
                    signMutation.mutate({
                      receiptTemplateId,
                      signatureData: patientData,
                      witnessSignatureData: witnessData ?? undefined,
                      signedByName: panel.signerName.trim() || undefined,
                    });
                  }}
                  onCancel={() => setPanel(null)}
                />
              ) : null}

              {panel?.kind === 'refund' ? (
                <RefundPaymentDialog
                  open
                  originalAmount={panel.originalAmount}
                  loading={refundMutation.isPending}
                  onClose={() => setPanel(null)}
                  onSubmit={(amount) =>
                    refundMutation.mutate({
                      transactionId: panel.transactionId,
                      amount,
                    })
                  }
                />
              ) : null}

            </>
          ) : null}
        </form>
      )}

      <RecurringDetailModal
        recurringId={membershipModalId}
        open={membershipModalId !== null}
        onClose={() => setMembershipModalId(null)}
        onChanged={() => {}}
      />

      {giftCardModalId !== null && (
        <GiftCardDetailModal
          open
          giftCardId={giftCardModalId}
          fromPaymentId={activePaymentId}
          readOnly={!canManageGiftCards}
          onClose={() => setGiftCardModalId(null)}
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey: ['payment-detail', activePaymentId] });
            void queryClient.invalidateQueries({ queryKey: ['gift-cards'] });
          }}
        />
      )}
    </Modal>
  );
}

// ─── Refund Payment dialog ──────────────────────────────────────────────────────

interface RefundPaymentDialogProps {
  open: boolean;
  /** Original (positive) transaction amount; partial refunds may not exceed it. */
  originalAmount: number;
  loading: boolean;
  onClose: () => void;
  /** `amount` undefined → full refund; a number → partial refund (dollars). */
  onSubmit: (amount: number | undefined) => void;
}

/**
 * Mirrors legacy patient_details.php updateTransaction()'s 'refund' branch:
 * a "Refund Payment" popup with a Full / Partial selector. Partial reveals a
 * dollar input that must be numeric, > 0, and <= the original amount (legacy
 * update_payment_transaction_authnet.php lines 603-617). Forward receives the
 * amount in cents via the V4 proxy; this dialog only deals in dollars.
 */
function RefundPaymentDialog({
  open,
  originalAmount,
  loading,
  onClose,
  onSubmit,
}: RefundPaymentDialogProps) {
  const [refundType, setRefundType] = useState<'full' | 'partial'>('full');
  const [amount, setAmount] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = () => {
    setValidationError(null);
    if (refundType === 'full') {
      onSubmit(undefined);
      return;
    }
    const trimmed = amount.trim();
    if (trimmed === '') {
      setValidationError('Please enter an amount to refund.');
      return;
    }
    const value = Number(trimmed);
    if (!Number.isFinite(value)) {
      setValidationError('Amount to refund must be a valid number.');
      return;
    }
    if (value <= 0) {
      setValidationError('Amount to refund must be greater than zero.');
      return;
    }
    if (originalAmount > 0 && value > originalAmount) {
      setValidationError('Amount to refund cannot exceed the original amount.');
      return;
    }
    onSubmit(value);
  };

  return (
    <Modal
      isOpen={open}
      onClose={loading ? () => {} : onClose}
      title="Refund Payment"
      size="sm"
      footer={(
        <>
          <Button size="sm" variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} loading={loading}>
            Submit Refund
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Original amount: <span className="font-medium text-slate-900">{formatCurrency(originalAmount)}</span>
        </p>

        <label className="block text-sm font-medium text-slate-700">
          Refund type
          <select
            value={refundType}
            onChange={(e) => {
              setRefundType(e.target.value as 'full' | 'partial');
              setValidationError(null);
            }}
            disabled={loading}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="full">Full Refund</option>
            <option value="partial">Partial Refund</option>
          </select>
        </label>

        {refundType === 'partial' ? (
          <label className="block text-sm font-medium text-slate-700">
            Amount
            <div className="mt-1 flex items-center gap-1">
              <span className="text-slate-500">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={loading}
                autoFocus
                className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </label>
        ) : null}

        {validationError ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {validationError}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

// ─── Unified Receipt Modal ──────────────────────────────────────────────────────

interface ReceiptModalProps {
  open: boolean;
  onClose: () => void;
  paymentId: number;
  receiptTemplates: ReceiptTemplate[];
  selectedTemplateId: number | null;
  onSelectTemplate: (id: number | null) => void;
  previewBlobUrl: string | null;
  onDirectLink: () => void;
  directLink: { fullUrl: string; shortUrl: string; filename: string } | null;
  directLinkLoading: boolean;
  showEmailOption: boolean;
  onSendEmail: (payload: { receiptTemplateId: number; subject?: string }) => void;
  emailSending: boolean;
}

function ReceiptModal({
  open,
  onClose,
  paymentId,
  receiptTemplates,
  selectedTemplateId,
  onSelectTemplate,
  previewBlobUrl,
  onDirectLink,
  directLink,
  directLinkLoading,
  showEmailOption,
  onSendEmail,
  emailSending,
}: ReceiptModalProps) {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [subject, setSubject] = useState('');
  const [defaultSubject, setDefaultSubject] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Fetch the HTML email preview whenever the email form opens or the
  // selected receipt template changes. The fetched HTML is injected into
  // the preview iframe via `srcDoc`; the returned `defaultSubject` is
  // pre-filled into the subject input so the staff member sees what the
  // recipient will see by default.
  useEffect(() => {
    if (!showEmailForm || !selectedTemplateId || !paymentId) {
      setPreviewHtml(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setEmailError(null);
    previewReceiptEmailHtml(paymentId, selectedTemplateId)
      .then((res) => {
        if (cancelled) return;
        setPreviewHtml(res.html);
        setDefaultSubject(res.defaultSubject);
        // Pre-fill only when the user hasn't typed their own subject yet;
        // avoid clobbering an in-progress edit when the template changes.
        setSubject((current) => (current.trim() === '' ? res.defaultSubject : current));
      })
      .catch((err) => {
        if (!cancelled) {
          setPreviewHtml(null);
          setEmailError(getErrorMessage(err, 'Failed to load email preview.'));
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showEmailForm, selectedTemplateId, paymentId]);

  const copyShortUrl = () => {
    if (directLink?.shortUrl) {
      navigator.clipboard?.writeText(directLink.shortUrl).catch(() => {});
    }
  };

  const handleSendEmail = () => {
    setEmailError(null);
    const receiptTemplateId = selectedTemplateId ?? receiptTemplates[0]?.templateId ?? 0;
    if (!receiptTemplateId) {
      setEmailError('Select a receipt template first.');
      return;
    }
    onSendEmail({
      receiptTemplateId,
      subject: subject.trim() || undefined,
    });
  };

  return (
    <Modal
      isOpen={open}
      onClose={emailSending ? () => {} : onClose}
      title="Receipt"
      size="xl"
      footer={(
        <>
          <Button size="sm" variant="ghost" onClick={onClose} disabled={emailSending}>
            Close
          </Button>
        </>
      )}
    >
      <div className="space-y-4 p-4">
        {/* Template selector + actions */}
        <div className="flex items-end gap-3">
          <label className="flex-1 text-sm">
            <span className="font-medium text-slate-700">Receipt template</span>
            <select
              value={selectedTemplateId ?? ''}
              onChange={(e) => onSelectTemplate(e.target.value ? Number(e.target.value) : null)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select template…</option>
              {receiptTemplates.map((t) => (
                <option key={t.templateId} value={t.templateId}>{t.title}</option>
              ))}
            </select>
          </label>
          <Button size="sm" variant="secondary" onClick={onDirectLink} loading={directLinkLoading} disabled={!selectedTemplateId}>
            <Link2 className="mr-1.5 h-3.5 w-3.5" />
            Direct Link
          </Button>
          {showEmailOption ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowEmailForm(!showEmailForm)}
              disabled={!selectedTemplateId}
            >
              <Mail className="mr-1.5 h-3.5 w-3.5" />
              {showEmailForm ? 'Hide Email' : 'Email'}
            </Button>
          ) : null}
        </div>

        {/* Direct Link */}
        {directLink ? (
          <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs">
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={directLink.shortUrl}
                className="flex-1 rounded border border-emerald-300 bg-white px-2 py-1.5 font-mono"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <Button size="sm" variant="ghost" onClick={copyShortUrl}>Copy</Button>
            </div>
          </div>
        ) : null}

        {/* Email form (expandable). The body of the email IS the HTML
            receipt below — no template selection, no PDF attachment. */}
        {showEmailForm && showEmailOption ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
            <h4 className="text-sm font-semibold text-blue-900">Email Receipt to Patient</h4>

            {emailError ? (
              <p className="text-xs text-red-600">{emailError}</p>
            ) : null}

            <label className="block text-sm">
              <span className="font-medium text-slate-700">Subject</span>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={defaultSubject || 'Payment Receipt'}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="mt-1 block text-xs text-slate-500">
                Leave blank to use the practice's default subject.
              </span>
            </label>

            <div className="text-sm">
              <div className="mb-1 font-medium text-slate-700">Preview</div>
              {previewLoading ? (
                <div className="flex h-[420px] items-center justify-center rounded border border-slate-200 bg-white">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : previewHtml ? (
                <iframe
                  title="Email preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  className="h-[480px] w-full rounded border border-slate-200 bg-white"
                />
              ) : (
                <div className="flex h-[420px] items-center justify-center rounded border border-dashed border-slate-300 bg-white">
                  <p className="text-sm text-slate-500">No preview available.</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end pt-1">
              <Button size="sm" onClick={handleSendEmail} loading={emailSending} disabled={!previewHtml}>
                Send Email
              </Button>
            </div>
          </div>
        ) : null}

        {/* PDF Preview — hidden while the user is composing the email so
            the HTML email preview above is the only visible artifact. */}
        {!showEmailForm && (
          previewBlobUrl ? (
            <iframe
              title="Receipt preview"
              src={previewBlobUrl}
              className="h-[400px] w-full rounded border border-slate-200 bg-white"
            />
          ) : (
            <div className="flex h-[200px] items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50">
              <p className="text-sm text-slate-500">
                {selectedTemplateId ? 'Loading PDF…' : 'Select a template to preview the receipt.'}
              </p>
            </div>
          )
        )}
      </div>
    </Modal>
  );
}

interface SignReceiptPanelProps {
  receiptTemplates: ReceiptTemplate[];
  selectedTemplateId: number | null;
  onSelectTemplate: (id: number | null) => void;
  signerName: string;
  onSignerNameChange: (v: string) => void;
  signedPdfUrl: string | null;
  loading: boolean;
  patientSigRef: React.MutableRefObject<SignaturePadFieldHandle | null>;
  witnessSigRef: React.MutableRefObject<SignaturePadFieldHandle | null>;
  onSubmit: () => void;
  onCancel: () => void;
}

function SignReceiptPanel({
  receiptTemplates,
  selectedTemplateId,
  onSelectTemplate,
  signerName,
  onSignerNameChange,
  signedPdfUrl,
  loading,
  patientSigRef,
  witnessSigRef,
  onSubmit,
  onCancel,
}: SignReceiptPanelProps) {
  const selectedTemplate =
    receiptTemplates.find((t) => t.templateId === selectedTemplateId) ?? receiptTemplates[0] ?? null;
  const showWitness = selectedTemplate?.witnessSignature ?? false;

  // Revoke the signed-PDF blob URL when this panel unmounts / next sign attempt
  useEffect(() => {
    return () => {
      if (signedPdfUrl) URL.revokeObjectURL(signedPdfUrl);
    };
  }, [signedPdfUrl]);

  return (
    <section className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-blue-900">Sign Receipt</h3>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={loading}>Cancel</Button>
      </div>

      {!signedPdfUrl ? (
        <>
          <label className="block text-sm">
            <span className="text-slate-700">Receipt template</span>
            <select
              value={selectedTemplateId ?? ''}
              onChange={(e) => onSelectTemplate(e.target.value ? Number(e.target.value) : null)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
            >
              <option value="">Select…</option>
              {receiptTemplates.map((t) => (
                <option key={t.templateId} value={t.templateId}>{t.title}</option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="text-slate-700">Signer name</span>
            <input
              type="text"
              value={signerName}
              onChange={(e) => onSignerNameChange(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
            />
          </label>

          <SignaturePadField ref={patientSigRef} label="Patient signature" />
          {showWitness ? (
            <SignaturePadField ref={witnessSigRef} label="Witness signature" />
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button size="sm" onClick={onSubmit} loading={loading}>Sign &amp; Generate PDF</Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs text-emerald-700">Signed receipt generated.</p>
          <iframe
            title="Signed receipt"
            src={signedPdfUrl}
            className="h-[600px] w-full rounded border border-slate-200 bg-white"
          />
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button size="sm" variant="ghost" onClick={onCancel}>Close</Button>
          </div>
        </>
      )}
    </section>
  );
}
